import { normalizeHex } from '../ColorPicker/palette';

/** An opaque sRGB colour, channels 0–255. */
export interface NxRgb {
  r: number;
  g: number;
  b: number;
}

/**
 * `#RGB` / `#RRGGBB` (with or without `#`, any case) as channels, or `null`.
 * Colours with transparency are rejected: their contrast depends on what lies
 * beneath.
 */
export function hexToRgb(hex: string | null | undefined): NxRgb | null {
  const n = normalizeHex(hex);
  if (!n) return null;
  return {
    r: parseInt(n.slice(1, 3), 16),
    g: parseInt(n.slice(3, 5), 16),
    b: parseInt(n.slice(5, 7), 16),
  };
}

function toRgb(color: string | NxRgb): NxRgb | null {
  return typeof color === 'string' ? hexToRgb(color) : color;
}

function linear(channel: number): number {
  const c = Math.max(0, Math.min(255, channel)) / 255;
  return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

/** WCAG relative luminance, 0 (black) – 1 (white); `null` for an unreadable colour. */
export function relativeLuminance(color: string | NxRgb): number | null {
  const rgb = toRgb(color);
  if (!rgb) return null;
  return 0.2126 * linear(rgb.r) + 0.7152 * linear(rgb.g) + 0.0722 * linear(rgb.b);
}

/**
 * WCAG contrast ratio of two colours, 1 – 21 (order does not matter); `null`
 * when either colour is unreadable. 4.5 is AA for body text, 3 for large
 * text and UI parts, 7 is AAA.
 */
export function contrastRatio(a: string | NxRgb, b: string | NxRgb): number | null {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  if (la === null || lb === null) return null;
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

export type NxContrastLevel = 'AAA' | 'AA' | 'AA-large' | 'fail';

/**
 * The WCAG level a ratio reaches for normal text: `AA-large` means only large
 * text (and UI parts) pass.
 */
export function contrastLevel(ratio: number | null): NxContrastLevel {
  if (ratio === null || !Number.isFinite(ratio)) return 'fail';
  if (ratio >= 7) return 'AAA';
  if (ratio >= 4.5) return 'AA';
  if (ratio >= 3) return 'AA-large';
  return 'fail';
}

export interface NxReadableTextOptions<L extends string = string, D extends string = string> {
  /** Text colour for dark backgrounds. Default `#FFFFFF`. */
  light?: L;
  /** Text colour for light backgrounds. Default `#000000`. */
  dark?: D;
}

/**
 * Of two text colours, the one with more contrast on `background` (a tie goes
 * to `light`) — e.g. the label on a user-picked calendar colour. An unreadable
 * background returns `dark`, the safe guess for a light page.
 */
export function readableTextOn<L extends string = '#FFFFFF', D extends string = '#000000'>(
  background: string | NxRgb,
  options: NxReadableTextOptions<L, D> = {}
): L | D {
  const light = (options.light ?? '#FFFFFF') as L;
  const dark = (options.dark ?? '#000000') as D;
  const onLight = contrastRatio(light, background);
  const onDark = contrastRatio(dark, background);
  if (onLight === null && onDark === null) return dark;
  if (onDark === null) return light;
  if (onLight === null) return dark;
  return onLight >= onDark ? light : dark;
}
